import {
  Avatar,
  Box,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import {
  SchedulingProps,
  SchedulingWarningsProps,
} from '../SchedulingInterface';
import { useEffect, useState } from 'react';
import { styles } from './styles';
import { AppError } from '@/utils/AppError';
import { useDoctorService } from '@/data/services/schedule/useDoctorService';
import { DoctorDTO } from '@/data/DTOs/schedule/DoctorDTO';
import { useTranslation } from 'react-i18next';
import { getInitials } from '@/utils/getInitials';
import IconUserDefault from '@/assets/scheduling/IconUserDefault.svg';

export const SchedulingDoctorsList = ({
  setSchedule,
  schedule,
  setLoading,
  setShowWarning,
  setWarningMessage,
  setIsValid,
  internalObject,
  setInternalObject,
}: SchedulingProps & SchedulingWarningsProps) => {
  const [doctors, setDoctors] = useState<DoctorDTO[]>([]);
  const { getDoctors } = useDoctorService();
  const { t } = useTranslation(['common, schedule']);

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    setIsValid(
      schedule.resourceId !== '-' &&
        doctors.some((doc) => doc.resourceId === schedule.resourceId)
    );
  }, [schedule.resourceId, doctors]);

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      let doctorsList: DoctorDTO[] = [];

      if (!schedule.locationId || schedule.appointmentTypeId == 2) {
        doctorsList = await getDoctors();

        const selectedDoctor = doctorsList.find(
          (doctor) => doctor.resourceId === schedule.resourceId
        );

        if (selectedDoctor?.isOnline && selectedDoctor.providerLocationId) {
          setSchedule({
            ...schedule,
            locationId: selectedDoctor.providerLocationId,
          });
        }
      }

      if (doctorsList.length === 0) {
        doctorsList = await getDoctors(schedule.locationId);
      }

      const providerDoctor = doctorsList.find(
        (doctor) => schedule.resourceId === doctor.providerId
      );

      if (providerDoctor) {
        setSchedule({
          ...schedule,
          resourceId: providerDoctor.resourceId,
        });
      }

      setDoctors(doctorsList);

      setInternalObject({ ...internalObject, allDoctors: doctorsList });
    } catch (error: AppError | any) {
      console.error('[ERROR] fetchDoctors:', error);
      setWarningMessage(error.message);
      setShowWarning(true);
    } finally {
      setLoading(false);
    }
  };

  const item = (doctor: DoctorDTO, index: number) => {
    const selected =
      schedule.resourceId == doctor.resourceId ||
      schedule.resourceId == doctor.providerId;

    const noPreference = doctor.resourceId == '';

    return (
      <ListItemButton
        sx={[
          styles.item,
          selected ? styles.itemFocused : styles.itemNotFocused,
        ]}
        key={index}
        selected={selected}
        onClick={() => {
          const noPreference = doctor.resourceId === '';
          const isOnlineAppointment = schedule.appointmentTypeId === 2;

          const locationToSet = noPreference
            ? schedule.locationId
            : isOnlineAppointment
            ? undefined
            : doctor.providerLocationId?.toString() || '';

          setSchedule({
            ...schedule,
            resourceId: doctor.resourceId,
            locationId: locationToSet ?? undefined,
            date: '',
            time: '',
          });

          setInternalObject({
            ...internalObject,
            doctorName: doctor.name,
          });
        }}

        // onClick={() => {
        //   const noPreference = doctor.resourceId === '';
        //   const locationToSet = noPreference
        //     ? schedule.locationId
        //     : doctor.providerLocationId?.toString() || '';

        //   setSchedule({
        //     ...schedule,
        //     resourceId: doctor.resourceId,
        //     locationId: locationToSet,
        //     date: '',
        //     time: '',
        //   });

        //   setInternalObject({
        //     ...internalObject,
        //     doctorName: doctor.name,
        //   });

        //   console.log(
        //     `[NAVEGAÇÃO] Próxima tela receberá: resourceId = '${
        //       doctor.resourceId
        //     }', locationId = '${locationToSet || 'NÃO DEFINIDO'}'`
        //   );
        // }}
      >
        <ListItemAvatar>
          <Avatar
            alt={doctor.name}
            src={doctor.profilePhotoUrl}
            sx={styles.photo}
          >
            {!noPreference ? (
              getInitials(doctor.name)
            ) : (
              <Box component="img" src={IconUserDefault} />
            )}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={`${!noPreference ? t('md') : ''} ${
            noPreference ? t(doctor.name) : doctor.name
          }`}
          secondary={
            !noPreference && (
              <Typography sx={styles.subtitleCard}>
                ME {doctor.nationalProviderId}
              </Typography>
            )
          }
          sx={styles.titleCard}
        />
      </ListItemButton>
    );
  };

  return (
    <List sx={styles.list}>
      {doctors.length === 0
        ? Array.from(new Array(5)).map((_, index) => (
            <Box key={index} sx={{ position: 'relative', mb: 1 }}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={70}
                sx={{ border: '1px solid #ccc', borderRadius: 1 }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 16,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Skeleton variant="circular" width={50} height={50} />
                <Skeleton
                  variant="text"
                  width={120}
                  height={20}
                  sx={{ ml: 2 }}
                />
              </Box>
            </Box>
          ))
        : doctors.map((doctor: DoctorDTO, index) => item(doctor, index))}
    </List>
  );
};
